import { useMemo } from 'react';
import { combine } from '../data/queries/combine';
import {
  useSecurityEvents,
  useSources,
  useRemediations,
  useThreatCandidates,
  useThreatElements,
  useThreatEvents,
} from '../data/queries/hooks';
import { computeFunnelCounters } from '../data/selectors/funnelCounters';
import {
  DEFAULT_FUNNEL_WINDOW,
  emptyFunnelCounters,
  type FunnelCounters,
  type FunnelWindow,
} from '../lib/funnel';

/**
 * The Overview counters, counted from the same cached records the Threat Feed and the Workspace read,
 * so the six rings are downloaded once per tick for the whole console, not once per screen.
 *
 * `loaded` is true once every ring has been read for the current organization. Until then `counters`
 * is an empty placeholder, and a screen must not present it as real zeros. `enabled` false reads
 * nothing, for the Demo band that shows its own figures.
 */
export function useFunnelCounters(enabled = true, funnelWindow: FunnelWindow = DEFAULT_FUNNEL_WINDOW) {
  const options = { enabled };
  const candidates = useThreatCandidates(options);
  const events = useSecurityEvents(options);
  const threatEvents = useThreatEvents(options);
  const elements = useThreatElements(options);
  const remediations = useRemediations(options);
  const sources = useSources(options);
  const state = combine([candidates, events, threatEvents, elements, remediations, sources]);

  // Counting looks back from "now": the moment the newest read stands for, so a tick that brings back
  // the same records still moves the window and the waiting times forward.
  const countedAt = state.updatedAt;
  const counters = useMemo<FunnelCounters>(() => {
    if (!state.ready) return emptyFunnelCounters();
    return computeFunnelCounters(
      {
        candidates: candidates.data ?? [],
        events: events.data ?? [],
        threatEvents: threatEvents.data ?? [],
        elements: elements.data ?? [],
        remediations: remediations.data ?? [],
        sources: sources.data ?? [],
      },
      countedAt || Date.now(),
      funnelWindow
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.ready,
    countedAt,
    funnelWindow,
    candidates.data,
    events.data,
    threatEvents.data,
    elements.data,
    remediations.data,
    sources.data,
  ]);

  if (!enabled) return { counters, loading: false, loaded: false, error: null as string | null };
  return {
    counters,
    loading: state.loading,
    loaded: state.ready,
    error: state.error ? 'Could not load the overview figures' : null,
  };
}
